import * as React from 'react';
import { BaseEdge, getBezierPath, type EdgeProps } from '@xyflow/react';
import { getSmoothStepPath } from '@xyflow/system';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';

type EdgeCurve = 'default' | 'smoothstep';

function ExecEdgeComponent(props: EdgeProps) {
  const rawCurve = ((props.data as any)?.curve ?? 'default') as 'default' | 'smoothstep' | 'step';
  const curve: EdgeCurve = rawCurve === 'smoothstep' ? 'smoothstep' : 'default';
  const onCurveChange = React.useCallback(
    (value: EdgeCurve) => {
      try {
        useCanvasStore.getState().patchEdge(props.id, { curve: value });
      } catch {}
    },
    [props.id],
  );
  const onRemove = React.useCallback(() => {
    try {
      useCanvasStore.getState().removeEdge(props.id);
    } catch {}
  }, [props.id]);
  const [path] = (
    curve === 'smoothstep' ? getSmoothStepPath(props)
    : getBezierPath(props)
  );
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <ExecEdgeTrigger
          edgeId={props.id}
          path={path}
          selected={props.selected ?? false}
          sourceX={props.sourceX}
          sourceY={props.sourceY}
          targetX={props.targetX}
          targetY={props.targetY}
        />
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ExecEdgeMenu curve={curve} onCurveChange={onCurveChange} onRemove={onRemove} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

const ExecEdge = React.memo(
  ExecEdgeComponent,
  (prev, next) =>
    prev.id === next.id &&
    prev.selected === next.selected &&
    prev.sourceX === next.sourceX &&
    prev.sourceY === next.sourceY &&
    prev.targetX === next.targetX &&
    prev.targetY === next.targetY &&
    ((prev.data as any)?.curve ?? 'default') === ((next.data as any)?.curve ?? 'default'),
);

export default ExecEdge;

type ExecEdgeTriggerProps = {
  edgeId: string;
  path: string;
  selected: boolean;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
} & React.ComponentPropsWithoutRef<'g'>;

const shallowEqual = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!Object.is(a[key], b[key])) return false;
  }
  return true;
};

const ExecEdgeTrigger = React.memo(
  React.forwardRef<SVGGElement, ExecEdgeTriggerProps>(function ExecEdgeTrigger(
    { edgeId, path, selected, sourceX, sourceY, targetX, targetY, ...rest },
    ref,
  ) {
    return (
      <g ref={ref} {...rest}>
        <BaseEdge id={edgeId} path={path} style={{ stroke: 'var(--edge-exec)', strokeWidth: 3 }} markerEnd="url(#rf__arrowhead)" />
        {selected && (
          <path
            d={path}
            stroke={'hsl(var(--primary))'}
            strokeOpacity={0.6}
            strokeWidth={8}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {selected && (
          <>
            <circle cx={sourceX} cy={sourceY} r={4.5} fill={'hsl(var(--primary))'} fillOpacity={0.7} />
            <circle cx={targetX} cy={targetY} r={4.5} fill={'hsl(var(--primary))'} fillOpacity={0.7} />
          </>
        )}
      </g>
    );
  }),
  (prev, next) =>
    prev.edgeId === next.edgeId &&
    prev.path === next.path &&
    prev.selected === next.selected &&
    prev.sourceX === next.sourceX &&
    prev.sourceY === next.sourceY &&
    prev.targetX === next.targetX &&
    prev.targetY === next.targetY &&
    shallowEqual(
      (({ edgeId, path, selected, sourceX, sourceY, targetX, targetY, ...restProps }) => restProps)(prev),
      (({ edgeId, path, selected, sourceX, sourceY, targetX, targetY, ...restProps }) => restProps)(next),
    ),
);

ExecEdgeTrigger.displayName = 'ExecEdgeTrigger';

type ExecEdgeMenuProps = {
  curve: EdgeCurve;
  onCurveChange: (value: EdgeCurve) => void;
  onRemove: () => void;
};

const ExecEdgeMenu = React.memo(function ExecEdgeMenu({
  curve,
  onCurveChange,
  onRemove,
}: ExecEdgeMenuProps) {
  const handleValueChange = React.useCallback(
    (value: string) => {
      onCurveChange(value === 'smoothstep' ? 'smoothstep' : 'default');
    },
    [onCurveChange],
  );

  const handleRemove = React.useCallback<React.ComponentProps<typeof ContextMenuItem>['onSelect']>(
    () => {
      onRemove();
    },
    [onRemove],
  );

  return (
    <>
      <ContextMenuLabel>Edge Type</ContextMenuLabel>
      <ContextMenuRadioGroup value={curve} onValueChange={handleValueChange}>
        <ContextMenuRadioItem value="default">Default</ContextMenuRadioItem>
        <ContextMenuRadioItem value="smoothstep">Smoothstep</ContextMenuRadioItem>
      </ContextMenuRadioGroup>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={handleRemove}>
        Remove Edge
      </ContextMenuItem>
    </>
  );
});

ExecEdgeMenu.displayName = 'ExecEdgeMenu';
